import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { t } from '../../../utils/translations';

const ProfileImageUpload = ({ 
  currentImage, 
  onImageChange, 
  onImageRemove,
  disabled = false,
  error 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      alert(t('officeFormComponents.pleaseSelectValidImage'));
      return;
    }

    // Validate file size (max 5MB)
    if (file?.size > 5 * 1024 * 1024) {
      alert(t('officeFormComponents.fileSizeMustBeLess'));
      return;
    }

    setIsUploading(true);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e?.target?.result;
      onImageChange(imageUrl, file);
      setIsUploading(false);
    };
    reader?.readAsDataURL(file);
  };

  const handleFileInputChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragActive(false);
    
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragActive(false);
  };

  const triggerFileInput = () => {
    fileInputRef?.current?.click();
  };

  const handleRemoveImage = () => {
    onImageRemove();
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="User" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            {t('officeFormComponents.profileImage')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('officeFormComponents.uploadProfessionalPhoto')}
          </p>
        </div>
      </div>

      {/* Current Image Preview */}
      {currentImage && (
        <div className="flex items-start space-x-4 p-4 bg-card border border-border rounded-lg">
          <div className="relative">
            <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted">
              <Image
                src={currentImage}
                alt={t('officeFormComponents.profilePhotoAlt')}
                className="w-full h-full object-cover"
              />
            </div>
            <Button
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
              onClick={handleRemoveImage}
              disabled={disabled || isUploading}
              iconName="X"
              iconSize={12}
            />
          </div>
          
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">{t('officeFormComponents.currentImage')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('officeFormComponents.clickXToRemove')}
            </p>
            <div className="flex space-x-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={triggerFileInput}
                disabled={disabled || isUploading}
                iconName="Upload"
                iconPosition="left"
                iconSize={14}
              >
                {t('officeFormComponents.replaceImage')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Area */}
      {!currentImage && (
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={!disabled ? triggerFileInput : undefined}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            disabled={disabled || isUploading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />

          <div className="space-y-4">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
              <Icon 
                name={isUploading ? "Loader2" : "Upload"} 
                size={24} 
                className={`text-muted-foreground ${isUploading ? 'animate-spin' : ''}`}
              />
            </div>

            <div>
              <p className="text-sm font-medium text-foreground">
                {isUploading ? t('officeFormComponents.uploadingImage') : t('officeFormComponents.uploadProfileImage')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('officeFormComponents.dragAndDropImage')}
              </p>
            </div>

            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={disabled || isUploading}
                loading={isUploading}
                iconName="Upload"
                iconPosition="left"
                iconSize={14}
              >
                {t('officeFormComponents.chooseFile')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Guidelines */}
      <div className="p-3 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
          <div className="text-xs text-muted-foreground space-y-1">
            <p>{t('officeFormComponents.recommendedSize')}</p>
            <p>{t('officeFormComponents.supportedFormats')}</p>
            <p>{t('officeFormComponents.maximumFileSize')}</p>
            <p>{t('officeFormComponents.useProfessionalHeadshot')}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center space-x-2 text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ProfileImageUpload;